import { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { useStateContext } from '../../contexts/ContextProvider';
import { getAcreditaciones } from '../../services/Acreditacion';
import { getCentroCosto } from '../../services/CentroCostos';
import { formatDate } from '../../formatters/date';
import DetailsIconButton from '../../components/IconButtons/DetailsIconButton';
import DetalleAcreditacion from '../../components/Acreditacion/DetalleAcreditacion';

const estadoBadge = (estado) => {
  const map = {
    'Pendiente':     'bg-yellow-100 text-yellow-700',
    'En Revisión':   'bg-blue-100 text-blue-700',
    'Aprobada':      'bg-green-100 text-green-700',
    'Rechazada':     'bg-red-100 text-red-700',
    'Salida de Obra':'bg-gray-100 text-gray-500',
  };
  return (
    <span className={`text-xs px-2 py-1 rounded-full font-medium ${map[estado] || 'bg-gray-100 text-gray-700'}`}>
      {estado}
    </span>
  );
};

const customStyles = {
  table:    { style: { backgroundColor: 'white', borderRadius: '8px' } },
  head:     { style: { fontSize: '14px', fontWeight: 'bold', color: '#2F336A', backgroundColor: '#F2F5FE', borderBottom: '1px solid #D9E1F2' } },
  headRow:  { style: { minHeight: '48px', borderBottomWidth: '1px' } },
  headCells:{ style: { paddingLeft: '12px', paddingRight: '12px', whiteSpace: 'nowrap' } },
  rows: {
    style: { fontSize: '13px', backgroundColor: 'white', minHeight: '52px', borderBottom: '1px solid #F3F4F6', transition: 'background-color 0.2s ease-in-out' },
    highlightOnHoverStyle: { backgroundColor: '#F9FAFB', borderBottomColor: '#E5E7EB', outline: 'none' },
  },
  cells: { style: { paddingLeft: '12px', paddingRight: '12px' } },
};

const Acreditacion = () => {
  const { handleClick, isClicked } = useStateContext();
  const [data, setData] = useState([]);
  const [centros, setCentros] = useState([]);
  const [seleccionado, setSeleccionado] = useState(null);

  const [filtroCentro,    setFiltroCentro]    = useState('ALL');
  const [filtroEstado,    setFiltroEstado]    = useState('ALL');
  const [filtroNroCarpeta,setFiltroNroCarpeta]= useState('');
  const [filtroEquipo,    setFiltroEquipo]    = useState('');

  const fetchData = async () => {
    const filtros = {};
    if (filtroEstado !== 'ALL') filtros.estado = filtroEstado;
    if (filtroCentro !== 'ALL') filtros.idCentro = filtroCentro;
    if (filtroNroCarpeta) filtros.numeroCarpeta = filtroNroCarpeta;
    if (filtroEquipo) filtros.codigoEquipo = filtroEquipo;

    const res = await getAcreditaciones(filtros);
    setData(res || []);
  };

  useEffect(() => {
    getCentroCosto().then(setCentros).catch(() => {});
  }, []);

  useEffect(() => { fetchData(); }, [filtroEstado, filtroCentro, filtroNroCarpeta, filtroEquipo]);

  const handleVer = (row) => {
    setSeleccionado(row);
    handleClick('detalleAcreditacion');
  };

  const handleRefresh = (opcion) => {
    if (opcion === 'REFRESH') fetchData();
  };

  const ultimaFecha = (historial = []) => {
    if (!historial.length) return '—';
    return formatDate(historial[historial.length - 1].fecha);
  };

  const columns = [
    {
      name: '',
      width: '40px',
      cell: (row) => <DetailsIconButton onClick={() => handleVer(row)} />,
    },
    {
      name: 'N° Carpeta',
      width: '110px',
      selector: row => row.numeroCarpeta,
      sortable: true,
    },
    {
      name: 'C.Costo',
      width: '100px',
      selector: row => row.idCentro?.codigo || '—',
      sortable: true,
    },
    {
      name: 'Proyecto',
      width: '280px',
      selector: row => row.idCentro?.nombreProyecto || '—',
      sortable: true,
    },
    {
      name: 'Máquina',
      width: '150px',
      selector: row => row.idMaquina?.nombreMaquina || '—',
      sortable: true,
    },
    {
      name: 'N° Interno',
      width: '130px',
      selector: row => row.codigoEquipo || '—',
      sortable: true,
    },
    {
      name: 'Serie',
      width: '130px',
      selector: row => row.serie || '—',
      sortable: true,
    },
    {
      name: 'Patente',
      width: '110px',
      selector: row => row.patente || '—',
      sortable: true,
    },
    {
      name: 'Último Cambio',
      width: '140px',
      selector: row => ultimaFecha(row.historial),
      sortable: true,
    },
    {
      name: 'Estado',
      width: '130px',
      selector: row => row.estado,
      sortable: true,
      cell: (row) => estadoBadge(row.estado),
    },
  ];

  const inputCls = 'text-xs border border-gray-300 p-2 rounded w-full';

  return (
    <>
      <div className="mx-4 lg:mx-12 mt-4">
        <div className="flex justify-between items-center px-4 py-2 border-b border-gray-200 bg-white rounded-t-md shadow-sm">
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse" />
            <p className="text-gray-800 font-semibold text-base md:text-lg">Acreditación</p>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-lg mt-2">
          <div className="flex justify-start items-center p-2 md:ml-6 md:mr-6 relative top-1">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 justify-between">
              <div>
                <label className="text-xs font-semibold mb-1 whitespace-nowrap block">C.Costo</label>
                <select className={inputCls} value={filtroCentro} onChange={e => setFiltroCentro(e.target.value)}>
                  <option value="ALL">Todos</option>
                  {centros.map(c => <option key={c._id} value={c._id}>{c.codigo} — {c.nombreProyecto}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold mb-1 block">Estado</label>
                <select className={inputCls} value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}>
                  <option value="ALL">Todos</option>
                  <option value="Pendiente">Pendiente</option>
                  <option value="Aprobada">Aprobada</option>
                  <option value="Rechazada">Rechazada</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold mb-1 whitespace-nowrap block">N° Carpeta</label>
                <input className={inputCls} type="number" placeholder="" value={filtroNroCarpeta} onChange={e => setFiltroNroCarpeta(e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-semibold mb-1 whitespace-nowrap block">N° Interno</label>
                <input className={inputCls} type="text" placeholder="" value={filtroEquipo} onChange={e => setFiltroEquipo(e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-lg items-end mt-4">
          <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
            <div className="flex flex-col">
              <DataTable
                columns={columns}
                data={data}
                customStyles={customStyles}
                pagination
                paginationPerPage={15}
                paginationRowsPerPageOptions={[10, 15, 25, 50]}
                highlightOnHover
                striped
                noHeader
                selectableRowsHighlight
                className="border border-gray-300 rounded-lg shadow-sm"
                noDataComponent="No hay registros de acreditación"
              />
            </div>
          </div>
        </div>
      </div>

      {isClicked.detalleAcreditacion && seleccionado && (
        <DetalleAcreditacion data={seleccionado} onChangeDatos={handleRefresh} />
      )}
    </>
  );
};

export default Acreditacion;
